import { useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { ICapturedImage } from '../../shared/interfaces/ICapturedImage';
import { captureVideoFrame } from '../../utils/room-shared';

type Props = {
  autoPlay?: boolean;
};
export const useCustomVideoPlayer = ({ autoPlay }: Props) => {
  const playerRef = useRef<ReactPlayer | null>(null);
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState<boolean>(false);
  const [isVideoReady, setIsVideoReady] = useState<boolean>(false);
  const [isBuffering, setIsBuffering] = useState<boolean>(false);
  const [currentVideoTime, setCurrentVideoTime] = useState<number>(0);
  const [volume, setVolume] = useState<number>(1);
  const [videoDuration, setVideoDuration] = useState<string>('');
  const [showControls, setShowControls] = useState<boolean>(false);
  const [videoProgress, setVideoProgress] = useState<number>(0);
  const [bufferedProgress, setBufferedProgress] = useState<number>(0);

  const [isSeeking, setIsSeeking] = useState<boolean>(false);

  const hideControlTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleReady = () => {
    const player = playerRef.current;
    setShowControls(true);

    if (player) {
      setVideoDuration(player.getDuration().toString());
      if (!isSeeking) {
        if (autoPlay) {
          setIsVideoPlaying(true);
        }
      }

      setIsVideoReady(true);
    }
  };
  const handlePlay = () => {
    const player = playerRef.current;

    if (player) {
      const currentTime = player.getCurrentTime();
      const duration = player.getDuration();
      if (currentTime > 0) {
        if (duration >= currentTime) {
          player.seekTo(currentVideoTime, 'seconds');
        }
      }
    }
    setIsVideoPlaying(true);
  };

  const handlePause = () => {
    setIsVideoPlaying(false);
  };

  const handleEnded = () => {
    setIsVideoPlaying(false);
    setCurrentVideoTime(0);
    setVideoProgress(0);
    setBufferedProgress(0);
    setIsSeeking(false);
  };
  const handleProgress = ({
    playedSeconds,
    played,
    loaded,
  }: {
    playedSeconds: number;
    played: number;
    loadedSeconds: number;
    loaded: number;
  }) => {
    const player = playerRef.current;
    if (player) {
      const currentTime = playedSeconds;
      setCurrentVideoTime(currentTime);
      setBufferedProgress(loaded * 100);
      const playedProgress = played * 100;
      setVideoProgress(playedProgress);
    }
  };

  const handleOpenFullScreen = () => {
    setIsFullScreen(true);
    document.documentElement.requestFullscreen();
  };
  const handleCloseFullScreen = () => {
    setIsFullScreen(false);
    document.exitFullscreen();
  };

  const handleSliderChange = (value: number) => {
    setIsSeeking(true);

    const player = playerRef.current;
    if (player) {
      setVideoProgress(value);
      const seekToValue = (value / 100) * player.getDuration();
      setCurrentVideoTime(seekToValue);

      player.seekTo(seekToValue, 'seconds');
    }
  };

  const handleStartScrub = () => {
    setIsSeeking(true);
  };
  const handleEndScrub = () => {};

  const handleVolumeChange = (value: number) => {
    setVolume(value);
  };

  const handleBufferStart = () => setIsBuffering(true);
  const handleBufferEnd = () => setIsBuffering(false);
  const handleCurrentVideoTime = (time: number) => {
    setCurrentVideoTime(time);
  };

  const handleVideoIsReady = (bool: boolean) => {
    setIsVideoReady(bool);
  };
  const handleVideoIsPlaying = (bool: boolean) => {
    setIsVideoPlaying(bool);
  };

  const handleCaptureImage = (): ICapturedImage | undefined => {
    const player = playerRef.current;
    if (player) {
      const videoElement = player.getInternalPlayer() as HTMLVideoElement;
      const captureFrame = captureVideoFrame(videoElement, 'jpeg');
      return captureFrame;
    }
  };

  useEffect(() => {
    if (isFullScreen) {
      document.body.style.width = '100vw';
      document.body.style.height = '100vh';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.width = '100%';
      document.body.style.height = '100%';
      document.body.style.overflow = 'visible';
    }
  }, [isFullScreen]);

  const handleEscape = () => {
    if (!document.fullscreenElement) {
      setIsFullScreen(false);
    }
  };

  useEffect(() => {
    window.addEventListener('fullscreenchange', handleEscape);
    return () => window.removeEventListener('fullscreenchange', handleEscape);
  }, []);

  const handleFullScreenMouseMove = () => {
    if (isFullScreen) {
      setShowControls(true);

      if (hideControlTimeout.current) {
        clearTimeout(hideControlTimeout.current);
      }
      hideControlTimeout.current = setTimeout(() => setShowControls(false), 2000);
    }
  };

  useEffect(() => {
    window.addEventListener('mousemove', handleFullScreenMouseMove);

    return () => window.removeEventListener('mousemove', handleFullScreenMouseMove);
  }, [isFullScreen]);

  return {
    playerRef,
    isFullScreen,
    isVideoPlaying,
    isVideoReady,
    isBuffering,
    currentVideoTime,
    videoProgress,
    volume,
    videoDuration,
    showControls,
    bufferedProgress,
    handleReady,
    handlePlay,
    handlePause,
    handleEnded,
    handleProgress,
    handleSliderChange,
    handleVolumeChange,
    handleOpenFullScreen,
    handleCloseFullScreen,
    handleBufferStart,
    handleBufferEnd,
    handleStartScrub,
    handleEndScrub,
    handleCurrentVideoTime,
    handleVideoIsReady,
    handleVideoIsPlaying,
    handleCaptureImage,
  };
};
